import {
  StockLevelOverwrite,
  useMaterialStorageDataQuery,
  useCreateStockLevelOverwrite,
  useIsMaterialUnmeasured,
  useStockOverwrites,
  StockOverwritesQueryKey
} from '@domain/material-storage'
import {mapToMaterialsById, useMaterials} from '@domain/materials'
import {useStockDevelopmentQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {Card, CardTitle} from '@hconnect/uikit/src/lib2'
import {usePlantStartOfToday, useUpmPlantContext, useUpmPlantTimezone} from '@hconnect/upm'
import {Box, useTheme} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {
  StockDevelopmentChartLegend,
  StockDevelopmentChartSkeleton,
  DatetimeValue
} from '@widgets/stock-card'
import moment from 'moment-timezone'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  StaticStockDevelopmentChart,
  StockOverwriteDialog
} from '../components/stockDevelopmentChart'
import {FEATURE_NAMESPACE} from '../consts'
import {useIncomingMaterialsQuery, useUrlParam, type MomentRange} from '../hooks'
import {useSelectedTimeframe} from '../providers'
import {getIncomingMaterialHourly} from '../selectors'
import {getListOfDays} from '../utils'

export const DetailedStockDevelopmentCard = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const materialIdParam = useUrlParam('materialId')
  const materialId = Number(materialIdParam)
  const {plantId: plantCode} = useUpmPlantContext()
  const {selectedTimeframe} = useSelectedTimeframe()
  if (!selectedTimeframe) {
    throw new Error('BUG: Selected timeframe is not defined')
  }
  const {spacing} = useTheme()
  const timezoneId = useUpmPlantTimezone()

  const startOfToday = usePlantStartOfToday()
  const queryClient = useQueryClient()

  const baseTimeframe: MomentRange = useMemo(
    () => [startOfToday.clone().subtract(15, 'days'), startOfToday.clone().add(15, 'days')],
    [startOfToday]
  )

  const {data: stockDevelopment, isLoading: isStockDevelopmentLoading} = useStockDevelopmentQuery({
    materialIds: [materialId],
    timeframe: baseTimeframe,
    selectedRange: selectedTimeframe
  })
  const {data: incomingMaterial, isLoading: isIncomingMaterialLoading} = useIncomingMaterialsQuery({
    materialId,
    timeframe: selectedTimeframe
  })
  const {data: storageData} = useMaterialStorageDataQuery({
    materialId
  })

  const {data: materialsById} = useMaterials({
    plantCode,
    options: {
      select: mapToMaterialsById
    }
  })

  const [stockOverwriteDialogParams, setStockOverwriteDialogOpen] = useState<
    {data: DatetimeValue<string, number>; overwrite: StockLevelOverwrite | undefined} | undefined
  >(undefined)
  const openStockOverwriteDialog = useCallback(
    (data: DatetimeValue<string, number>, overwrite: StockLevelOverwrite | undefined) => {
      setStockOverwriteDialogOpen({data, overwrite})
    },
    []
  )
  const {data: isMaterialUnmeasured, isLoading: isMaterialUnmeasuredIsLoading} =
    useIsMaterialUnmeasured(materialId, plantCode)

  const [start, end] = selectedTimeframe
  const {data: stockOverwrites} = useStockOverwrites({
    plantCode,
    materialId,
    from: start.toISOString(),
    to: end.toISOString(),
    options: {
      enabled: !isMaterialUnmeasuredIsLoading && !!isMaterialUnmeasured
    }
  })

  const stockData = useMemo(() => {
    if (stockDevelopment === undefined || isMaterialUnmeasuredIsLoading) return undefined
    if (!isMaterialUnmeasured || stockOverwrites !== undefined)
      return {...stockDevelopment[materialId], stockOverwrites}
    return undefined
  }, [
    stockDevelopment,
    stockOverwrites,
    isMaterialUnmeasured,
    isMaterialUnmeasuredIsLoading,
    materialId
  ])

  const {mutate: createStockLevelOverwrite, isPending: createStockLevelOverwriteIsLoading} =
    useCreateStockLevelOverwrite()

  const onCloseStockOverwriteDialog = useCallback(() => setStockOverwriteDialogOpen(undefined), [])
  const onStockOverwrite = useCallback(
    (data: DatetimeValue<string, number>) => {
      createStockLevelOverwrite(
        {
          plantCode,
          date: data.datetime,
          level: data.value,
          materialId
        },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: [StockOverwritesQueryKey],
              refetchType: 'all'
            })
            onCloseStockOverwriteDialog()
          }
        }
      )
    },
    [createStockLevelOverwrite, onCloseStockOverwriteDialog, materialId, plantCode, queryClient]
  )

  const listOfDays = getListOfDays({start: selectedTimeframe[0], end: selectedTimeframe[1]})

  const shouldShowChartSkeleton =
    isStockDevelopmentLoading || isIncomingMaterialLoading || !stockData
  const chartHeight = spacing(50)
  const incomingMaterialHourly = incomingMaterial && getIncomingMaterialHourly(incomingMaterial)

  const isDisabledDateEditing = useMemo(
    () =>
      !!stockData?.stockOverwrites?.find((overwrite) =>
        stockOverwriteDialogParams?.data?.datetime
          ? moment(overwrite.measuredAt)
              .tz(timezoneId)
              .isSame(moment(stockOverwriteDialogParams?.data?.datetime).tz(timezoneId))
          : false
      ),
    [stockData?.stockOverwrites, stockOverwriteDialogParams?.data?.datetime, timezoneId]
  )

  return (
    <Card
      headerContent={
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <CardTitle variant="h3" sx={{mb: 0}}>
            {t('stock.stockDevelopment')}
          </CardTitle>
          <StockDevelopmentChartLegend />
        </Box>
      }
      {...dataTestId('stock_development_card')}
    >
      {shouldShowChartSkeleton ? (
        <StockDevelopmentChartSkeleton height={chartHeight} />
      ) : (
        <StaticStockDevelopmentChart
          timezoneId={timezoneId}
          listOfDays={listOfDays}
          stockData={stockData}
          incomingMaterialHourly={incomingMaterialHourly}
          openStockOverwriteDialog={isMaterialUnmeasured ? openStockOverwriteDialog : undefined}
          minTargetLevels={
            storageData && {
              week: storageData.minTargetLevel,
              weekend: storageData.minTargetLevelWeekend
            }
          }
          deadStockLevel={storageData?.deadStockLevel}
          storageCapacity={storageData?.storageCapacity}
          height={parseInt(chartHeight)}
        />
      )}
      {stockOverwriteDialogParams && materialsById && (
        <StockOverwriteDialog
          isSaving={createStockLevelOverwriteIsLoading}
          stock={stockOverwriteDialogParams?.data}
          overwrite={stockOverwriteDialogParams?.overwrite}
          material={materialsById[materialId]}
          disabledDateEditing={isDisabledDateEditing}
          onOverwrite={onStockOverwrite}
          onClose={onCloseStockOverwriteDialog}
        />
      )}
    </Card>
  )
}
